import flow from 'lodash/flow';
import { updateisOtherTouched, updateOtherValue } from '../shared/reducer';
import {
  commonQuestionState,
  updateWhy,
  updateMatrixWhy,
} from '../response/reducer';
import {
  getServerValidationErrors,
  mapPropsToValidators,
  matrixOtherValidator,
  validate,
} from '../validators';
import {
  matrixMaxChoicesValidator,
  forceExclusiveValue,
  selectListOtherValidator,
  selectListRequiredValidator,
  enforceRanking,
  matrixForceExclusiveValue,
  matrixRequiredValidator,
  maxChoicesValidator,
  minChoicesValidator,
  matrixMinChoicesValidator,
} from './validators';
import { prefillMatrixSelect, prefillWhy } from '../prefillAnswers';

const reducer = props =>
  props.matrix ? matrixReducer(props) : simpleReducer(props);
export default reducer;

// Simple

const updateSingleSelectValues = action => state => ({
  ...state,
  values: [action.value],
});
const updateMultiSelectValues = action => state => ({
  ...state,
  values: action.checked
    ? state.values.concat([action.value])
    : state.values.filter(value => value !== action.value),
});
const updateisTouched = () => state => ({
  ...state,
  isTouched: true,
});

const addOtherState = props => state => ({
  ...state,
  isOtherValid: true,
  isOtherTouched: false,
  otherValue: props.otherValue || '',
});

const addSimpleState = props => state => ({
  ...state,
  values: props.tmap || [],
  validators: mapPropsToValidators(
    {
      add_dont_know: props.add_dont_know,
      add_none: props.add_none,
      add_other: props.add_other,
      required: props.required,
      min_choices: props.min_choices,
      max_choices: props.max_choices,
    },
    {
      add_dont_know: forceExclusiveValue('dont_know'),
      add_none: forceExclusiveValue('none'),
      add_other: selectListOtherValidator,
      required: selectListRequiredValidator,
      min_choices: minChoicesValidator(props),
      max_choices: maxChoicesValidator(props),
    }
  ),
});

const simpleReducer = props => (state, action) => {
  if (state === undefined) {
    return flow(
      commonQuestionState(props),
      getServerValidationErrors(props),
      addOtherState(props),
      addSimpleState(props)
    )({});
  }

  switch (action.type) {
    case 'CONTROL_CHANGE': {
      const updater =
        props._type === 'Survey::SingleSelect'
          ? updateSingleSelectValues
          : updateMultiSelectValues;
      return flow(updater(action), validate(action))(state);
    }

    case 'OTHER_CHANGE':
      return flow(updateOtherValue(action), validate(action))(state);

    case 'SELECT_LIST_BLUR':
      return flow(updateisTouched(), validate(action))(state);

    case 'OTHER_BLUR':
      return flow(updateisOtherTouched, validate(action))(state);

    case 'WHY_CHANGE':
      return flow(updateWhy(action), validate(action))(state);

    case 'SUBMIT':
      return flow(
        updateisTouched(),
        updateisOtherTouched,
        validate(action)
      )(state);

    default:
      return state;
  }
};

// Matrix

const addMatrixState = props => state => ({
  ...state,
  rows: props.rows.map((row, rowIndex) => ({
    name: row.name,
    index: rowIndex,
    isTouched: false,
    other: row.other,
    values: prefillMatrixSelect(props.tmap, row.name),
    why: {
      isTouched: false,
      value: prefillWhy(row.name, props.tmap),
    },
  })),
  validators: mapPropsToValidators(
    {
      add_dont_know: props.add_dont_know,
      add_none: props.add_none,
      add_other: props.add_other,
      ranking: props.ranking,
      required: props.required,
      min_choices: props.min_choices,
      max_choices: props.max_choices,
    },
    {
      add_dont_know: matrixForceExclusiveValue('dont_know'),
      add_none: matrixForceExclusiveValue('none'),
      add_other: matrixOtherValidator,
      ranking: enforceRanking,
      required: matrixRequiredValidator,
      min_choices: matrixMinChoicesValidator(props),
      max_choices: matrixMaxChoicesValidator(props),
    }
  ),
});

const matrixReducer = props => (state, action) => {
  if (state === undefined) {
    return flow(
      commonQuestionState(props),
      getServerValidationErrors(props),
      addOtherState(props),
      addMatrixState(props)
    )({});
  }

  switch (action.type) {
    case 'MATRIX_BUTTON_CHANGE': {
      const updateRows =
        props._type === 'Survey::SingleSelect'
          ? updateRadioRows
          : updateCheckboxRows;
      return flow(updateRows(action), validate(action))(state);
    }
    case 'MATRIX_SELECT_CHANGE':
      return flow(updateRadioRows(action), validate(action))(state);

    case 'MATRIX_BLUR':
      return flow(updateRowsAreTouched(action), validate(action))(state);

    case 'OTHER_CHANGE':
      return flow(updateOtherValue(action), validate(action))(state);

    case 'OTHER_BLUR':
      return flow(updateisOtherTouched, validate(action))(state);

    case 'MATRIX_WHY_CHANGE':
      return flow(updateMatrixWhy(action), validate(action))(state);

    case 'SUBMIT':
      return flow(updateisOtherTouched, touchAllRows, validate(action))(state);

    default:
      return state;
  }
};

const updateCheckboxRows = action => state => ({
  ...state,
  rows: state.rows.map(row => updateCheckboxRow(row, action)),
});

const updateCheckboxRow = (state, action) =>
  state.index === action.rowIndex
    ? {
        ...state,
        values: action.checked
          ? state.values.concat([action.value])
          : state.values.filter(v => v !== action.value),
      }
    : state;

const updateRadioRows = action => state => ({
  ...state,
  rows: state.rows.map(row => updateRadioRow(row, action)),
});

const updateRadioRow = (state, action) =>
  state.index === action.rowIndex
    ? {
        ...state,
        values: [action.value],
      }
    : state;

const updateRowsAreTouched = action => state => ({
  ...state,
  rows: state.rows.map(row => updateRowIsTouched(row, action)),
});

const updateRowIsTouched = (state, action) =>
  state.index === action.rowIndex
    ? {
        ...state,
        isTouched: true,
      }
    : state;

const touchAllRows = state => ({
  ...state,
  rows: state.rows.map(row => ({
    ...row,
    isTouched: true,
  })),
});
